import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { CircleAlert, Trash2 } from 'lucide-react';
import { getErrorMessage, hasProblemType, keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { Alert, AlertDescription, AlertTitle, Button, ConfirmDialog, toast } from '@blimo/ui';

/**
 * G-17 / F-101: „Löschen“ in der Personen-Detailansicht (`parties.delete`). Das Backend löscht nur Personen ohne Verweise;
 * sonst 409 `party-in-use` mit `references: [{ key, label, count }]` – die Liste wird hier angezeigt.
 * @param {{ party: { id: number, display_name?: string } }} props
 */
export function DeletePartyButton({ party, onBlocked }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  async function confirm() {
    try {
      await api.admin.parties.remove(party.id);
      queryClient.removeQueries({ queryKey: keys.admin.detail('parties', party.id) });
      queryClient.invalidateQueries({ queryKey: ['blimo', 'admin', 'parties', 'list'] });
      toast.success('Person gelöscht');
      navigate({ to: '/personen' });
    } catch (e) {
      onBlocked(
        hasProblemType(e, 'party-in-use')
          ? { title: 'Löschen nicht möglich – die Person wird noch verwendet', references: e.problem?.references ?? [] }
          : { title: 'Löschen nicht möglich', message: getErrorMessage(e, 'Die Person konnte nicht gelöscht werden.'), references: [] },
      );
    }
  }

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Trash2 aria-hidden="true" /> Löschen
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={`${party.display_name || 'Person'} löschen?`}
        description="Die Person wird mit ihren Kontaktwegen, Adressen und Einwilligungen endgültig gelöscht. Das ist nur möglich, wenn sie nirgends mehr verwendet wird (Eigentum, Mietverhältnisse, Benutzerkonto, Objektrollen, Herkunft aus Cobra/WinCasa/Migration …)."
        confirmLabel="Endgültig löschen"
        destructive
        onConfirm={confirm}
      />
    </>
  );
}

/** @param {{ blocked: { title: string, message?: string, references: Array<{ key: string, label: string, count: number }> }|null }} props */
export function DeletePartyBlockedAlert({ blocked }) {
  if (!blocked) return null;
  return (
    <Alert variant="destructive" className="mb-3">
      <CircleAlert aria-hidden="true" />
      <AlertTitle>{blocked.title}</AlertTitle>
      <AlertDescription>
        {blocked.message && <p>{blocked.message}</p>}
        {blocked.references.length > 0 && (
          <>
            <p>Verwendet in:</p>
            <ul className="list-disc pl-5" aria-label="Verweise auf die Person">
              {blocked.references.map((r) => (
                <li key={r.key}>
                  {r.label} <span className="numeric">({r.count})</span>
                </li>
              ))}
            </ul>
            <p className="mt-1">Personen aus Quellsystemen bitte dort löschen; Dubletten über die Dubletten-Prüfung zusammenführen.</p>
          </>
        )}
      </AlertDescription>
    </Alert>
  );
}
